import { useId } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Switch } from 'antd';
import { ApiOutlined, CodeOutlined, DesktopOutlined, DisconnectOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';

import { Tile } from '../../../components/shared';
import { Badge, Button, Empty } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { maLoi } from '../../../lib/errors';
import { DUONG_KET_NOI, LOAI_LUONG, QUYEN } from '../constants';
import { laChuaKhaiKetNoi } from '../hooks/useRpa';
import type { LoaiWorkflow } from '../types';

/**
 * Trạng thái lỗi của một vùng dữ liệu RPA. Ba trường hợp nói riêng, vì bấm Thử lại ở đó không bao giờ khác đi:
 * - `RPA_OPENCORE_NOT_CONFIGURED`: tenant chưa khai (hoặc đang tắt, thiếu JWT) kết nối OpenCore — hướng dẫn sang tab
 *   Kết nối OpenCore thay vì báo lỗi chung. Câu của máy chủ nói đúng thiếu gì, hiện nguyên văn.
 * - `API_UNAVAILABLE`: module chưa chạy trên máy chủ này.
 * - còn lại: câu lỗi (nguyên văn của máy chủ) kèm `Thử lại`.
 */
export function LoiRpa({ loi, onThuLai }: { loi: unknown; onThuLai?: () => void }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const duongDan = useDuongDan();
  const coKetNoi = useModulePermission(QUYEN.ketNoiDoc);

  if (laChuaKhaiKetNoi(loi)) {
    return (
      <Empty
        icon={DisconnectOutlined}
        title={t('rpa.chuaKhaiTieuDe')}
        description={dichLoi(loi)}
        action={
          coKetNoi ? (
            <Link
              to={duongDan(DUONG_KET_NOI)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
            >
              <ApiOutlined aria-hidden />
              {t('rpa.moKetNoi')}
            </Link>
          ) : (
            <p className="max-w-md text-center text-xs text-content-muted">{t('rpa.nhoQuanTri')}</p>
          )
        }
      />
    );
  }

  if (maLoi(loi) === 'API_UNAVAILABLE') {
    return (
      <Empty
        icon={ApiOutlined}
        title={t('rpa.chuaSanSangTieuDe')}
        description={t('rpa.chuaSanSangMoTa')}
        action={
          onThuLai ? (
            <Button variant="outline" size="sm" icon={<ReloadOutlined />} onClick={onThuLai}>
              {t('chung.thuLai')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <div className="p-4" role="alert">
      <div className="rounded-lg border border-danger-100 bg-danger-50 p-4 text-sm text-danger-700">
        <p className="font-medium">{t('rpa.loiTai')}</p>
        <p className="mt-1 break-words">{dichLoi(loi)}</p>
        {onThuLai && (
          <Button variant="outline" size="sm" className="mt-3" icon={<ReloadOutlined />} onClick={onThuLai}>
            {t('chung.thuLai')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Màn thiếu quyền đọc: trạng thái khoá, nói quyền nào cần — không gọi API rồi nhận `403`. */
export function KhongCoQuyen({ quyen }: { quyen: string }) {
  const { t } = useT();
  return (
    <Tile>
      <Empty
        icon={LockOutlined}
        title={t('rpa.khongCoQuyenTieuDe')}
        description={t('rpa.khongCoQuyenMoTa', { quyen })}
      />
    </Tile>
  );
}

/** Nhãn loại luồng — nhóm phân loại của `Badge` (`LOAI_LUONG` ở `constants.ts`). */
export function BadgeLoaiLuong({ loai }: { loai: LoaiWorkflow }) {
  const { t } = useT();
  const l = LOAI_LUONG[loai];
  return (
    <Badge variant={l.variant} className="whitespace-nowrap">
      <IconLoaiLuong loai={loai} />
      {t(l.khoa)}
    </Badge>
  );
}

export function IconLoaiLuong({ loai }: { loai: LoaiWorkflow }) {
  return loai === 'script' ? <CodeOutlined aria-hidden /> : <DesktopOutlined aria-hidden />;
}

/** Một dòng cài đặt bật/tắt: nhãn và giải thích bên trái, công tắc bên phải; bấm nhãn là bật/tắt. */
export function DongBatTat({
  nhan,
  goiY,
  bat,
  onDoi,
  voHieu,
}: {
  nhan: string;
  goiY?: string;
  bat: boolean;
  onDoi: (bat: boolean) => void;
  voHieu?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-default px-3 py-2.5">
      <label htmlFor={id} className="min-w-0 cursor-pointer text-sm text-content-primary">
        {nhan}
        {goiY && <span className="mt-0.5 block text-xs text-content-muted">{goiY}</span>}
      </label>
      <Switch id={id} checked={bat} onChange={onDoi} disabled={voHieu} className="shrink-0" />
    </div>
  );
}

/** Tiêu đề khối trong một `Tile`: tên, mô tả và hành động bên phải. */
export function DauKhoi({ tieuDe, moTa, hanhDong }: { tieuDe: ReactNode; moTa?: string; hanhDong?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-default px-4 py-3">
      <div className="min-w-0">
        <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-content-primary">{tieuDe}</h2>
        {moTa && <p className="text-xs text-content-muted">{moTa}</p>}
      </div>
      {hanhDong && <div className="flex shrink-0 flex-wrap items-center gap-2">{hanhDong}</div>}
    </div>
  );
}
